import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StoreMallDirectory from "@mui/icons-material/StoreMallDirectory";
import { useNavigate } from "react-router-dom";
import FileSaver from "file-saver";
import ExcelJS from "exceljs";
import { AuthState } from "context/AuthContext";
import { SbtRoleAccess } from "data/storeBudgetData";

const formatCurrency = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;

const formatDisplayDate = (value) => {
  if (!value) return "-";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const day = String(value.getDate()).padStart(2, "0");
    const month = String(value.getMonth() + 1).padStart(2, "0");
    return `${day}-${month}-${value.getFullYear()}`;
  }
  const datePart = String(value).split("T")[0];
  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${parsed.getFullYear()}`;
};

const ROW_HEIGHT = 52;
const HEADER_HEIGHT = 48;
const FOOTER_HEIGHT = 56;
const MAX_VISIBLE_ROWS = 12;
const HEIGHT_BUFFER = 8;
const PROJECT_ID_WIDTH = 210;
const ACTION_WIDTH = 72;

const GRID_MIN_HEIGHT = HEADER_HEIGHT + ROW_HEIGHT + FOOTER_HEIGHT + HEIGHT_BUFFER;
const GRID_MAX_HEIGHT = HEADER_HEIGHT + ROW_HEIGHT * MAX_VISIBLE_ROWS + FOOTER_HEIGHT + HEIGHT_BUFFER;

const getGridHeight = (rowCount) => {
  if (rowCount <= 0) return GRID_MIN_HEIGHT;
  const visibleRows = Math.min(rowCount, MAX_VISIBLE_ROWS);
  return Math.min(
    Math.max(HEADER_HEIGHT + visibleRows * ROW_HEIGHT + FOOTER_HEIGHT + HEIGHT_BUFFER, GRID_MIN_HEIGHT),
    GRID_MAX_HEIGHT
  );
};

const cardShell = {
  borderRadius: 2,
  border: "1px solid #e5e7eb",
  backgroundColor: "#fff",
};

const exportColumns = [
  { id: "project_id", label: "Project Id" },
  { id: "store_name", label: "Store Name" },
  { id: "store_code", label: "Store Code" },
  { id: "channel", label: "Channel" },
  { id: "project", label: "Project" },
  { id: "city", label: "City" },
  { id: "state", label: "State" },
  { id: "work_type", label: "Work Type" },
  { id: "store_opening_date", label: "Opening Date" },
  { id: "carpet_area", label: "Carpet Area" },
  { id: "std_per_sq_feet", label: "STD Per Sq Feet" },
  { id: "total_budget", label: "Total Budget" },
  { id: "po_amount", label: "PO Amount" },
  { id: "so_amount", label: "Service PO" },
  { id: "ap_amount", label: "AP Voucher" },
  { id: "grn_amount", label: "GRN" },
  { id: "canceled_amount", label: "Cancelled PO" },
  { id: "pending_po_amount", label: "Pending PO" },
  { id: "pending_amount", label: "Balance to Pay" },
  { id: "status", label: "Status" },
];

const middleColumns = [
  { id: "store_name", label: "Store Name", minWidth: 180 },
  { id: "store_code", label: "Store Code", minWidth: 120 },
  { id: "channel", label: "Channel", minWidth: 110 },
  { id: "project", label: "Project", minWidth: 130 },
  { id: "city", label: "City", minWidth: 120 },
  { id: "state", label: "State", minWidth: 120 },
  { id: "work_type", label: "Work Type", minWidth: 130 },
  { id: "store_opening_date", label: "Opening Date", minWidth: 140 },
  { id: "carpet_area", label: "Carpet Area", minWidth: 130, numeric: true },
  { id: "std_per_sq_feet", label: "STD Per Sq Feet", minWidth: 150, currency: true },
  { id: "total_budget", label: "Total Budget", minWidth: 150, currency: true, tone: "budget" },
  { id: "po_amount", label: "PO Amount", minWidth: 150, currency: true },
  { id: "so_amount", label: "Service PO", minWidth: 140, currency: true },
  { id: "ap_amount", label: "AP Voucher", minWidth: 140, currency: true },
  { id: "grn_amount", label: "GRN Amount", minWidth: 140, currency: true },
  { id: "canceled_amount", label: "Cancelled PO", minWidth: 160, currency: true, tone: "muted" },
  { id: "pending_po_amount", label: "Pending PO", minWidth: 150, currency: true, tone: "warn" },
  { id: "pending_amount", label: "Balance to Pay", minWidth: 160, currency: true, tone: "pending", hint: "Amount still payable to vendors: GRN + Service PO − AP Voucher" },
  { id: "status", label: "Status", minWidth: 110 },
];

const headerCellSx = {
  backgroundColor: "#eff6ff",
  fontWeight: 700,
  fontSize: 12,
  color: "#1e3a8a",
  whiteSpace: "nowrap",
  borderRight: "1px solid #dbeafe",
  borderBottom: "1px solid #bfdbfe",
  height: HEADER_HEIGHT,
  py: 0,
  px: 1.25,
  textAlign: "center",
};

const bodyCellSx = {
  borderRight: "1px solid #f1f5f9",
  borderBottom: "1px solid #eef2f7",
  height: ROW_HEIGHT,
  py: 0,
  px: 1.25,
  textAlign: "center",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: 220,
  backgroundColor: "#fff",
  fontSize: 13,
};

const stickyLeftHeaderSx = {
  ...headerCellSx,
  position: "sticky",
  left: 0,
  zIndex: 5,
  minWidth: PROJECT_ID_WIDTH,
  width: PROJECT_ID_WIDTH,
  maxWidth: PROJECT_ID_WIDTH,
  borderRight: "1px solid #93c5fd",
  boxShadow: "2px 0 8px -3px rgba(15, 23, 42, 0.12)",
};

const stickyLeftBodySx = {
  ...bodyCellSx,
  position: "sticky",
  left: 0,
  zIndex: 3,
  minWidth: PROJECT_ID_WIDTH,
  width: PROJECT_ID_WIDTH,
  maxWidth: PROJECT_ID_WIDTH,
  borderRight: "1px solid #bfdbfe",
  boxShadow: "2px 0 8px -3px rgba(15, 23, 42, 0.1)",
};

const stickyRightHeaderSx = {
  ...headerCellSx,
  position: "sticky",
  right: 0,
  zIndex: 5,
  minWidth: ACTION_WIDTH,
  width: ACTION_WIDTH,
  maxWidth: ACTION_WIDTH,
  borderRight: "none",
  borderLeft: "1px solid #93c5fd",
  boxShadow: "-2px 0 8px -3px rgba(15, 23, 42, 0.12)",
};

const stickyRightBodySx = {
  ...bodyCellSx,
  position: "sticky",
  right: 0,
  zIndex: 3,
  minWidth: ACTION_WIDTH,
  width: ACTION_WIDTH,
  maxWidth: ACTION_WIDTH,
  borderRight: "none",
  borderLeft: "1px solid #bfdbfe",
  boxShadow: "-2px 0 8px -3px rgba(15, 23, 42, 0.1)",
};

const dateSortFields = new Set(["store_opening_date", "updated_at", "created_at", "submitted_at"]);

const getSortValue = (row, field) => {
  if (field === "status") return row?.status === "submitted" ? "Closed" : "Open";
  return row?.[field];
};

const compareRows = (a, b, field, direction) => {
  const av = getSortValue(a, field);
  const bv = getSortValue(b, field);

  let result = 0;
  if (dateSortFields.has(field)) {
    const aTime = av ? new Date(av).getTime() : 0;
    const bTime = bv ? new Date(bv).getTime() : 0;
    result = aTime - bTime;
  } else {
    const aNum = Number(av);
    const bNum = Number(bv);
    if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && av !== "" && bv !== "" && av != null && bv != null) {
      result = aNum - bNum;
    } else {
      result = String(av ?? "").localeCompare(String(bv ?? ""), undefined, { sensitivity: "base" });
    }
  }

  return direction === "desc" ? -result : result;
};

const getExportCellValue = (row, columnId) => {
  if (columnId === "status") return row?.status === "submitted" ? "Closed" : "Open";
  if (columnId === "store_opening_date") return formatDisplayDate(row?.store_opening_date);
  const value = row?.[columnId];
  return value == null || value === "" ? "" : value;
};

const downloadTableExcel = async (rows, fileName) => {
  if (!rows?.length) return;

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Store Budgets");
  sheet.columns = exportColumns.map((col) => ({
    header: col.label,
    key: col.id,
    width: Math.max(String(col.label).length + 4, 16),
  }));
  sheet.getRow(1).font = { bold: true };
  sheet.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFDBEAFE" } };
  rows.forEach((row) => {
    const data = {};
    exportColumns.forEach((col) => {
      data[col.id] = getExportCellValue(row, col.id);
    });
    sheet.addRow(data);
  });
  const buffer = await workbook.xlsx.writeBuffer();
  FileSaver.saveAs(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    fileName
  );
};

const currencyColor = (column, value) => {
  if (column.tone === "budget") return "#1d4ed8";
  if (column.tone === "warn") return "#d97706";
  if (column.tone === "muted") return "#64748b";
  if (column.tone === "pending") return Number(value) < 0 ? "#dc2626" : "#0f766e";
  return "#0f172a";
};

const StatCard = ({ label, value, color, bg }) => (
  <Box sx={{ flex: 1, minWidth: 140, px: 1.75, py: 1.5, ...cardShell, backgroundColor: bg }}>
    <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.4} sx={{ textTransform: "uppercase", fontSize: 10 }}>
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight={800} sx={{ color, lineHeight: 1.25, mt: 0.35 }}>
      {value}
    </Typography>
  </Box>
);

const renderMiddleCell = (row, column) => {
  if (column.id === "store_opening_date") return formatDisplayDate(row?.store_opening_date);
  if (column.currency) {
    const value = row?.[column.id];
    return (
      <Typography variant="body2" noWrap fontWeight={600} sx={{ color: currencyColor(column, value), fontVariantNumeric: "tabular-nums" }}>
        {formatCurrency(value)}
      </Typography>
    );
  }
  if (column.id === "status") {
    const submitted = row?.status === "submitted";
    return (
      <Chip
        label={submitted ? "Closed" : "Open"}
        size="small"
        sx={{
          height: 22,
          fontWeight: 700,
          fontSize: 11,
          backgroundColor: submitted ? "#ecfdf5" : "#fffbeb",
          color: submitted ? "#047857" : "#b45309",
          border: `1px solid ${submitted ? "#a7f3d0" : "#fde68a"}`,
        }}
      />
    );
  }
  const value = row?.[column.id];
  return value == null || value === "" ? "-" : String(value);
};

export default function ListStoreBudget() {
  const [budgetList, setBudgetList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("updated_at");
  const [order, setOrder] = useState("desc");
  const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
  const navigate = useNavigate();
  const { user } = AuthState();
  const roleData = SbtRoleAccess(user);

  const handleDownload = useCallback(
    async (e, row) => {
      e.stopPropagation();
      try {
        setLoadingOpen(true);
        const res = await pythonVmInstance({
          url: `/store-budget-tracker/report/download-store-budget?project_id=${row?.project_id}`,
          headers: { "Content-Type": "application/json" },
          method: "GET",
          responseType: "blob",
        });
        const url = URL.createObjectURL(res.data);
        FileSaver.saveAs(url, `${row?.project_id}_store_budget.csv`);
      } catch (error) {
        setSnackBarOpen(true);
        setSnackBar({
          ...snackBar,
          message: "Failed to download file",
          severity: "error",
          anchorOrigin: { horizontal: "center", vertical: "top" },
        });
      } finally {
        setLoadingOpen(false);
      }
    },
    [setLoadingOpen, setSnackBar, setSnackBarOpen, snackBar]
  );

  const handleCreateBudgetClick = () => {
    navigate(`/explore/store_budget_tracker/create_store_budget`);
  };

  const handleRowClick = (row) => {
    navigate(`/explore/store_budget_tracker/create_store_budget`, {
      state: { data: row },
    });
  };

  const fetchBudgets = async () => {
    try {
      setLoadingOpen(true);
      const res = await pythonVmInstance({
        url: `/store-budget-tracker/store-budget`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      setBudgetList((res?.data || []).map((row, i) => ({ ...row, id: row?.id || `budget-${i}` })));
    } catch {
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: "Failed to fetch store budgets",
        severity: "error",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
    } finally {
      setLoadingOpen(false);
    }
  };

  useEffect(() => {
    fetchBudgets();
  }, []);

  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return budgetList.filter((row) => {
      const submitted = row?.status === "submitted";
      if (statusFilter === "open" && submitted) return false;
      if (statusFilter === "closed" && !submitted) return false;
      if (!query) return true;
      return [
        row?.project_id,
        row?.store_name,
        row?.store_code,
        row?.channel,
        row?.project,
        row?.city,
        row?.state,
        row?.work_type,
        formatDisplayDate(row?.store_opening_date),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [budgetList, searchText, statusFilter]);

  const sortedRows = useMemo(() => {
    const rows = [...filteredRows];
    rows.sort((a, b) => compareRows(a, b, orderBy, order));
    return rows;
  }, [filteredRows, order, orderBy]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sortedRows]);

  useEffect(() => {
    setPage(0);
  }, [searchText, statusFilter, rowsPerPage]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setOrderBy(field);
    setOrder("asc");
  };

  const hasListFilter = Boolean(searchText.trim()) || statusFilter !== "all";
  const rowsToDownload = hasListFilter
    ? sortedRows
    : [...budgetList].sort((a, b) => compareRows(a, b, orderBy, order));

  const handleDownloadTable = async () => {
    if (!rowsToDownload.length) {
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: hasListFilter ? "No filtered store budgets to download" : "No store budgets to download",
        severity: "warning",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
      return;
    }

    const dateStamp = new Date().toISOString().split("T")[0];
    const fileName = hasListFilter
      ? `store_budgets_filtered_${dateStamp}.xlsx`
      : `store_budgets_all_${dateStamp}.xlsx`;
    await downloadTableExcel(rowsToDownload, fileName);
    setSnackBarOpen(true);
    setSnackBar({
      ...snackBar,
      message: hasListFilter
        ? `Downloaded ${rowsToDownload.length} filtered store budget${rowsToDownload.length === 1 ? "" : "s"}`
        : `Downloaded all ${rowsToDownload.length} store budget${rowsToDownload.length === 1 ? "" : "s"}`,
      severity: "success",
      anchorOrigin: { horizontal: "center", vertical: "top" },
    });
  };

  const countSource = hasListFilter ? filteredRows : budgetList;
  const totals = useMemo(
    () =>
      countSource.reduce(
        (acc, row) => {
          acc.budget += Number(row.total_budget) || 0;
          acc.po += Number(row.po_amount) || 0;
          acc.pending += Number(row.pending_amount) || 0;
          return acc;
        },
        { budget: 0, po: 0, pending: 0 }
      ),
    [countSource]
  );
  const gridHeight = useMemo(() => getGridHeight(pagedRows.length), [pagedRows.length]);

  const statusChips = [
    { id: "all", count: budgetList.length },
    { id: "open", count: budgetList.filter((row) => row?.status !== "submitted").length },
    { id: "closed", count: budgetList.filter((row) => row?.status === "submitted").length },
  ];

  return (
    <Box padding={2} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
      <Box sx={{ ...cardShell, overflow: "hidden", width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          gap={1.5}
          sx={{ px: 2.5, py: 2, backgroundColor: "#eff6ff", borderBottom: "1px solid #dbeafe" }}
        >
          <Stack direction="row" alignItems="center" gap={1.5} minWidth={0}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                backgroundColor: "#dbeafe",
                color: "#1d4ed8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <StoreMallDirectory />
            </Box>
            <Box minWidth={0}>
              <Typography variant="h6" fontWeight={800} color="#0f172a">
                Store Budgets
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Open a row to view or update. Download uses the current search and status filter, or all rows if none are applied.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<DownloadOutlinedIcon />}
              onClick={handleDownloadTable}
              disabled={!rowsToDownload.length}
              sx={{ backgroundColor: "#fff", textTransform: "none", fontWeight: 700 }}
            >
              {hasListFilter ? "Download Filtered" : "Download All"}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateBudgetClick}
              disabled={roleData?.roles === "VIEWER"}
              sx={{ textTransform: "none", fontWeight: 700, boxShadow: "none" }}
            >
              Create Store Budget
            </Button>
          </Stack>
        </Stack>

        <Stack spacing={2} sx={{ p: 2.5 }}>
          <Stack direction="row" gap={1.25} flexWrap="wrap" alignItems="stretch">
            {statusChips.map((chip) => {
              const selected = statusFilter === chip.id;
              const colors = {
                all: { color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
                open: { color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
                closed: { color: "#047857", bg: "#ecfdf5", border: "#a7f3d0" },
              }[chip.id];
              return (
                <Box
                  key={chip.id}
                  onClick={() => setStatusFilter(chip.id)}
                  sx={{
                    flex: 1,
                    minWidth: 120,
                    px: 1.75,
                    py: 1.5,
                    ...cardShell,
                    backgroundColor: colors.bg,
                    border: `1px solid ${selected ? colors.color : colors.border}`,
                    boxShadow: selected ? `inset 0 0 0 1px ${colors.color}` : "none",
                    cursor: "pointer",
                  }}
                >
                  <Typography variant="caption" fontWeight={700} letterSpacing={0.4} sx={{ textTransform: "uppercase", fontSize: 10, color: colors.color }}>
                    {chip.id === "all" ? (hasListFilter && statusFilter === "all" ? "Filtered" : "All") : chip.id === "open" ? "Open" : "Closed"}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ color: colors.color, lineHeight: 1.25, mt: 0.35 }}>
                    {chip.count}
                  </Typography>
                </Box>
              );
            })}
            <StatCard label="Total Budget" value={formatCurrency(totals.budget)} color="#1d4ed8" bg="#eff6ff" />
            <StatCard label="PO Amount" value={formatCurrency(totals.po)} color="#4f46e5" bg="#eef2ff" />
            <StatCard
              label="Balance to Pay"
              value={formatCurrency(totals.pending)}
              color={totals.pending < 0 ? "#dc2626" : "#0f766e"}
              bg={totals.pending < 0 ? "#fef2f2" : "#f0fdfa"}
            />
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            gap={1.5}
          >
            <TextField
              size="small"
              placeholder="Search project, store, city, channel..."
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.secondary" }} fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText("")} aria-label="Clear search">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                maxWidth: { md: 380 },
                width: "100%",
                "& .MuiOutlinedInput-root": { backgroundColor: "#fff", borderRadius: 1.5 },
              }}
            />
          </Stack>

          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              height: gridHeight,
              minHeight: GRID_MIN_HEIGHT,
              maxHeight: GRID_MAX_HEIGHT,
              border: "1px solid #dbeafe",
              borderRadius: 1.5,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#fff",
            }}
          >
            <TableContainer sx={{ flex: 1, overflow: "auto" }}>
              <Table stickyHeader size="small" sx={{ minWidth: 1960, borderCollapse: "separate", borderSpacing: 0 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={stickyLeftHeaderSx}>
                      <TableSortLabel
                        active={orderBy === "project_id"}
                        direction={orderBy === "project_id" ? order : "asc"}
                        onClick={() => handleSort("project_id")}
                      >
                        Project Id
                      </TableSortLabel>
                    </TableCell>
                    {middleColumns.map((column) => (
                      <TableCell key={column.id} sx={{ ...headerCellSx, minWidth: column.minWidth }}>
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : "asc"}
                          onClick={() => handleSort(column.id)}
                        >
                          {column.hint ? (
                            <Tooltip title={column.hint}>
                              <span>{column.label}</span>
                            </Tooltip>
                          ) : (
                            column.label
                          )}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell sx={stickyRightHeaderSx}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={middleColumns.length + 2} align="center" sx={{ height: ROW_HEIGHT * 3, border: 0 }}>
                        <Stack alignItems="center" spacing={0.75} py={2}>
                          <StoreMallDirectory sx={{ color: "#93c5fd", fontSize: 36 }} />
                          <Typography variant="body2" fontWeight={700} color="#334155">
                            No store budgets found
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {hasListFilter ? "Try a different search or status filter." : "Create a store budget to get started."}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedRows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => handleRowClick(row)}
                        sx={{
                          cursor: "pointer",
                          "&:nth-of-type(even) td": { backgroundColor: "#f8fbff" },
                          "&:hover td": { backgroundColor: "#eff6ff" },
                        }}
                      >
                        <TableCell className="pinned-cell" sx={stickyLeftBodySx}>
                          <Tooltip title={row?.project_id || ""} placement="top-start">
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ width: "100%", fontWeight: 700, color: "#1d4ed8", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 }}
                            >
                              {row?.project_id || "-"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        {middleColumns.map((column) => (
                          <TableCell key={column.id} sx={{ ...bodyCellSx, minWidth: column.minWidth }}>
                            {renderMiddleCell(row, column)}
                          </TableCell>
                        ))}
                        <TableCell className="pinned-cell" sx={stickyRightBodySx} onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Download project CSV">
                            <IconButton
                              size="small"
                              onClick={(e) => handleDownload(e, row)}
                              sx={{ color: "#1d4ed8", backgroundColor: "#eff6ff", "&:hover": { backgroundColor: "#dbeafe" } }}
                            >
                              <DownloadOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={sortedRows.length}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{
                borderTop: "1px solid #dbeafe",
                minHeight: FOOTER_HEIGHT,
                flexShrink: 0,
                backgroundColor: "#f8fbff",
              }}
            />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
